<template>
  <v-app id="dashboard">
    <navigation></navigation>
    <v-main class="grey lighten-3">
      <v-container>
        <v-row>
          <v-col cols="md-4">
            <v-sheet rounded outlined class="pa-6" min-height="100%">
              <h4 class="mb-4">Filter</h4>
              <p class="text-caption grey--text text--darken-1 mb-4">
                Die Liste rechts wird nach Name, Gültigkeit und Status eingeschränkt (Name/Gültigkeit:
                Teiltreffer, nicht groß-/kleinschreibungsabhängig).
              </p>
              <v-text-field
                  v-model.trim="filterName"
                  label="Name"
                  dense
                  outlined
                  clearable
                  hide-details
                  class="mb-4"
                  placeholder="Interner Name, Titel, Ersteller …"
              />
              <v-text-field
                  v-model.trim="filterValidity"
                  label="Gültigkeit"
                  dense
                  outlined
                  clearable
                  hide-details
                  class="mb-4"
                  placeholder="z. B. 01.12. oder Datumsbereich"
              />
              <v-select
                  v-model="filterStatus"
                  :items="statusFilterOptions"
                  item-text="text"
                  item-value="value"
                  label="Status"
                  dense
                  outlined
                  clearable
                  hide-details
                  placeholder="Alle Status"
              />
              <v-btn
                  v-if="hasActiveFilters"
                  text
                  small
                  color="primary"
                  class="mt-3 px-0"
                  @click="clearFilters"
              >
                Filter zurücksetzen
              </v-btn>
            </v-sheet>
          </v-col>

          <!-- Users -->
          <v-col>
            <v-sheet min-height="100%" outlined class="pa-6">

              <v-btn :to="{ name: 'work_create' }" color="primary" class="float-right">
                Neues Produkt erstellen
              </v-btn>
              <h4 class="py-2">Produkte</h4>

              <div
                  v-if="hasActiveFilters"
                  class="text-caption grey--text text--darken-1 mb-2"
              >
                {{ filteredWorksData.length }} von {{ worksData.length }} Produkten
              </div>

              <div class="mt-4"></div>

              <v-simple-table>
                <thead>
                <tr>
                  <th class="text-left" style="min-width: 200px">Produkt</th>
                  <th class="text-left">Gültigkeit</th>
                  <th class="text-left">Status</th>
                  <th class="text-right" aria-label="Aktionen"></th>
                </tr>
                </thead>

                <tbody>
                <tr v-for="work in filteredWorksData" :key="work.id">
                  <td>
                    <div class="d-flex align-center py-2">
                      <img
                          v-if="work.image_src"
                          :src="work.image_src"
                          class="mr-3 flex-shrink-0"
                          style="width: 72px; height: 72px; object-fit: contain;"
                      />
                      <div style="min-width: 0">
                        <strong>{{ work.internal_name ? work.internal_name : 'Produkt ' + work.id }}</strong>
                        <div
                            v-if="work.user && work.user.name"
                            class="text-caption grey--text text--darken-1 mt-1"
                        >
                          {{ work.user.name }}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td>
                    {{ work.validity }}
                  </td>
                  <td>
                    {{ workStatusLabel(work.status) }}
                  </td>
                  <td class="text-right bg">
                    <v-btn
                        small
                        color="primary"
                        :to="`/work/${work.id}`"
                    >
                      Produkt bearbeiten
                    </v-btn>
                  </td>
                </tr>
                </tbody>
              </v-simple-table>

              <div
                  v-if="hasActiveFilters && !filteredWorksData.length"
                  class="text-body-2 grey--text text-center py-8"
              >
                Keine Produkte entsprechen den Filtern.
              </div>

              <!-- Paginator (bei Filter nur die aktuell geladene Seite; rpp groß → meist eine Seite) -->
              <div v-if="!hasActiveFilters" class="text-center mt-4">
                <v-pagination
                    v-model="page"
                    :length="works.last_page || 1"
                    @input="fetchWorks"
                ></v-pagination>
              </div>
              <div
                  v-else-if="works.last_page > 1"
                  class="text-caption grey--text text-center mt-4"
              >
                Filter wirkt auf die aktuell geladene Seite. Filter leeren, um andere Seiten zu wählen.
              </div>
            </v-sheet>
          </v-col>

        </v-row>


      </v-container>
    </v-main>
  </v-app>
</template>

<script>
import Navigation from "./Navigation.vue";

export default {
  components: { Navigation },
  data: () => ({
    page: 1,
    filterName: "",
    filterValidity: "",
    filterStatus: null,
  }),
  computed: {
    works() {
      return this.$store.getters.works;
    },
    worksData() {
      return (this.works && this.works.data) ? this.works.data : [];
    },
    hasActiveFilters() {
      return Boolean(this.filterName)
        || Boolean(this.filterValidity)
        || (this.filterStatus != null && this.filterStatus !== "");
    },
    statusFilterOptions() {
      const set = new Set(["PUBLIC", "DRAFT"]);
      this.worksData.forEach((w) => {
        if (w.status) {
          set.add(w.status);
        }
      });
      const order = ["PUBLIC", "DRAFT"];
      const sorted = [...set].sort(
        (a, b) => order.indexOf(a) - order.indexOf(b) || String(a).localeCompare(String(b)),
      );
      return sorted.map((s) => ({
        text: this.workStatusLabel(s),
        value: s,
      }));
    },
    filteredWorksData() {
      const nameQ = this.filterName.toLowerCase();
      const valQ = this.filterValidity.toLowerCase();
      const statusF = this.filterStatus;
      return this.worksData.filter((work) => {
        if (statusF != null && statusF !== "" && work.status !== statusF) {
          return false;
        }
        const title = (work.internal_name || work.name || `Produkt ${work.id}` || "").toLowerCase();
        const userName = (work.user && work.user.name) ? work.user.name.toLowerCase() : "";
        const validity = String(work.validity != null ? work.validity : "").toLowerCase();
        const nameOk = !nameQ || title.includes(nameQ) || userName.includes(nameQ);
        const valOk = !valQ || validity.includes(valQ);
        return nameOk && valOk;
      });
    },
  },
  async mounted() {
    if(localStorage.getItem('marketing')==='0') {
      this.$router.push({name: "dashboard"});
      return ;
    }
    this.loading = true;
    this.fetchWorks(1);
  },
  methods: {
    workStatusLabel(status) {
      const labels = { PUBLIC: "Öffentlich", DRAFT: "Entwurf" };
      if (status == null || status === "") {
        return "—";
      }
      return labels[status] || String(status);
    },
    clearFilters() {
      this.filterName = "";
      this.filterValidity = "";
      this.filterStatus = null;
    },
    async fetchWorks(page = 1) {
      this.page = page;
      window.scrollTo(0, 0);
      await this.$store.dispatch("getWorks", { page, rpp: 5000 });
    },
  },
};
</script>
